import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { FavoriteToggle } from '../../../common/react/components/FavoriteToggle/FavoriteToggle';
import { DevicePreviewShowPreviewEvent, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { Telemetry } from '../../../commonInfra/services/Telemetry';
import { Projects, ProjectsResource } from '../../../projects/resources/Projects';
import { Views } from '../../../views/resources/Views';
import { DataAlerts } from '../../../dda/resources/DataAlerts';
import { DevicePreviewModalBuilder } from '../../../devicePreview/utils/DevicePreviewModalBuilder';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { CommonErrorCode } from '../../../ts/helpers/ErrorCodes';
import { isCurrentUrlViz } from '../../../ts/helpers/UrlHelpers';
import { ViewDefinition } from '../../../ts/models/ContentDefinitions';
import { IDataAlertInfo } from '../../../ts/models/IDataAlertInfo';
import { IViewDetailInfo } from '../../../ts/models/IViewInfo';
import { IExplorableItemTypeInfo, INamedItemInfo } from '../../../ts/models/resourceInfoParts';
import { IViz, VizType } from '../../../ts/models/Viz';
import { DataSources } from '../../../ts/resources/Datasources';
import { IViewActions } from '../../../ts/ServerApi/types/IViewActions';
import { ServerService } from '../../../ts/services/ServerService';
import { VizService } from '../../../ts/services/VizService';
import { enterSpace, setSpaceInfoItem } from '../../actions/spaces/EnterSpaceAction';
import { setVizPath } from '../../actions/spaces/SetVizPathAction';
import { appStore } from '../../AppStore';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '../../content/breadcrumbs/BreadcrumbHelpers';
import { VizViewerWidgetsBar } from '../../content/VizViewerWidgetsBar';
import { VizPathInitialState } from '../../reducers/VizPathReducer';
import { selectLayoutType } from '../../selectors/LayoutTypeSelector';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { selectVizPath } from '../../selectors/VizPathSelector';
import { ServiceLocator } from '../../ServiceLocator';
import { LayoutType } from '../../state/LayoutType';
import { SpaceInfo } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { RequestAccessError } from '../RouterHelpers/RequestAccessError';
import { RouterError, RouterErrorOwnProps } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '../SpaceTelemetry';
import { sendVisitAlertFromEmailTelemetry } from './VizSpaceTelemetry';
import { VizUrl } from './VizUrlHelpers';

/**
 * This class renders tbViz, which is the container for a viz.
 * VPC and the viz communicate via the url and the parameters set on it. For example, when the viz is done
 * loading it sends a message and we update the url to have the instance query parameter (:iid=#). A lot of
 * java E2E tests wait for this to know that the viz is ready for interaction.
 * tbViz also creates the viz url for the _iframe_ that holds the viz. For example, it adds a device parameter
 * just for the iframe so the viz knows which device-specific-dashboard to show.
 * When a user navigates to: http://localhost:4000/#/views/DataAlertsWorkbook/DSDDashboard?:editDataAlert=89&:original_view=y&:iid=17
 * The iframe url might be:
 *  http://localhost:4000/views/DataAlertsWorkbook/DSDDashboard?:embed=y&:showVizHome=n&:toolbar=top&%3AeditDataAlert=89&%3Aoriginal_view=y&%3Adevice=tablet&%3AopenAuthoringInTopWindow=true&%3AbrowserBackButtonUndo=true&%3AcommentingEnabled=true&%3AreloadOnCustomViewSave=true&%3AshowAppBanner=false&%3AisVizPortal=true&:apiID=host1#navType=0&navSrc=Opt
 * which specifies information about commenting, device, and more.
 * It's very delicate and some url parameters should cause a re-render of the whole viz page, and some should not.
 * That's why this class has so much code.
 */

export interface VizSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly vizPathFromUrl: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly renderSpace: (angularContext: AngularContext, setupScope: (scope: ng.IScope) => ng.IScope) => JSX.Element;
  readonly reloadVizSpy_forTestOnly?: () => void;
}

export interface VizSpaceProviderStateProps {
  readonly vizPath: string;
  readonly sessionSiteUrlName: string;
  readonly layoutType: LayoutType;
  readonly isRequestAccessEnabled: boolean;
}

export interface VizSpaceProviderDispatchProps {
  readonly setVizPath: (vizPath: string) => void;
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
}

export interface VizSpaceProviderAngularProps {
  readonly server: ServerService;
  readonly dataSources: DataSources;
  readonly vizService: VizService;
  readonly projects: ProjectsResource;
  readonly location: ng.ILocationService;
}

export interface VizSpaceProviderState {
  readonly viewInfo: IViewDetailInfo;
  readonly errorCode: number;
  readonly hasRoutingError: boolean;  // routing error only!
  readonly errorVizPath: string;
  readonly hasDataAlertError: boolean;
  readonly dataAlertRouterErrorProps: RouterErrorOwnProps;
  readonly dataAlert: IDataAlertInfo;  // only if editing a data alert
}

const initialNoErrorState: VizSpaceProviderState = {
  viewInfo: undefined,
  errorCode: undefined,
  hasRoutingError: false,
  hasDataAlertError: false,
  dataAlertRouterErrorProps: undefined,
  errorVizPath: undefined,
  dataAlert: undefined
};

const template = `<div ng-include="'vizViewer.html'"></div>`;

export interface IVizProperties {
  view?: IViewDetailInfo;
}

interface IVizViewerScope extends ng.IScope {
  viz: IViz;
  vizProps: IVizProperties;
  favoriteToggleProps: FavoriteToggle.Props;
  vizViewerWidgetsBarProps: VizViewerWidgetsBar.Props;
  hasError: () => boolean;
  hasViewInfo: () => boolean;
  onVizSized: () => void;
  vizInitialized: boolean;
  shouldShowWhoHasSeenThisView: () => boolean;
  allowedAction: IViewActions;
  dataAlert: IDataAlertInfo;
  firstViewPathOfPageLoad: IViewDetailInfo;

  // used to display a RouterError
  // for the 'dont-have-access' errors
  displayRoutingError: boolean;  // Viz-not-found, NOT data alerts errors!
  displayDataAlertRoutingError: boolean;

  updateVizProviderState: (newState: Partial<VizSpaceProviderState>) => void;
}


export const renderVizSpace = (angularContext: AngularContext, setupScope: (scope: ng.IScope) => ng.IScope): JSX.Element => {
  // the parent div here is important to ensure AngularDirective plays nice when unmounting
  return (
    <div>
      <AngularDirective
        template={template}
        angularContext={angularContext}
        setupScope={setupScope}
      />
    </div>
  );
};

export type VizSpaceProviderPropsWithoutAngular = VizSpaceProviderOwnProps & VizSpaceProviderStateProps & VizSpaceProviderDispatchProps;
export type VizSpaceProviderProps = VizSpaceProviderPropsWithoutAngular & VizSpaceProviderAngularProps;

export class VizSpaceProvider extends React.Component<VizSpaceProviderProps, VizSpaceProviderState> {

  private spaceScope: IVizViewerScope = null;
  private unmounted: boolean = false;
  private firstViewInfoOfPageLoad: IViewDetailInfo = undefined;

  public constructor(props: VizSpaceProviderProps) {
    super(props);
    this.state = initialNoErrorState;
  }

  public render() {
    const dataAlertIdFromUrl: string = VizUrl.getVizParams(this.props.routeLocationSearch)[UrlParamKeys.EditDataAlert];

    if (this.state.hasDataAlertError) {
      return <RouterError {...this.state.dataAlertRouterErrorProps} />;

    } else if (this.state.hasRoutingError) {
      if (this.props.isRequestAccessEnabled) {
        return <RequestAccessError
          url={this.props.urls.exploreUrl()}
          contentIdentifier={this.state.errorVizPath}
          contentType={'view'}
          errorCode={this.state.errorCode}/>;
      }

      // old error page
      return <RouterError urls={this.props.urls} errorType="view"/>;

    } else if (!this.state.viewInfo) {
      return null;

  } else if (!_.isUndefined(dataAlertIdFromUrl) && (_.isUndefined(this.state.dataAlert) || this.state.dataAlert.id !== dataAlertIdFromUrl)) {
      // don't render viz if we are editing a data alert until the data alert for the current data alert id (in the url)
      // is fetched, so that we don't render the wrong viz for the alert device type
      return null;
    }

    // have view info and no errors, render the viz
    return this.props.renderSpace(this.props.angularContext, this.setupScope);
  }

  public async componentDidMount() {
    // set initial viz path
    this.props.setVizPath(this.props.vizPathFromUrl);

    await this.getViewInfoAndUpdateBrowserTitleAndGetDataAlert();
  }

  public componentWillUnmount() {
    this.unmounted = true;
    this.firstViewInfoOfPageLoad = undefined;
    this.props.setVizPath(''); // reset store
  }

  private getViewInfoAndUpdateBrowserTitleAndGetDataAlert = async () => {
    try {
      const viewInfo: IViewDetailInfo = await Views.getByPath(this.props.vizPathFromUrl);
      BrowserTitleService.setVizTitle(viewInfo.name, viewInfo.workbook.name);
      this.props.setSpaceInfoItem({item: viewInfo, spaceType: 'view', subspaceName: undefined});
      this.firstViewInfoOfPageLoad = viewInfo;

      try {
        if (!_.isUndefined(viewInfo)) {
          const currDataAlertId = VizUrl.getVizParams(this.props.routeLocationSearch)[UrlParamKeys.EditDataAlert];
          if (currDataAlertId) {
            // data alert info needs to be fetched before we update the state and render the viz
            const dataAlert: IDataAlertInfo = await DataAlerts.getById(currDataAlertId);
            this.handleGetDataAlertResponse(dataAlert, viewInfo);
          } else {

            // not editDataAlert parameter so just set the viewInfo on the state
            this.updateState({...initialNoErrorState, viewInfo: viewInfo});
          }
        }
        return viewInfo;

      } catch (e) {
        this.handleGetDataAlertReturnsError(viewInfo);
        return viewInfo;
      }

    } catch (errorCode) {
      // fetching viewInfo failed-- show view routing error
      this.handleViewGetByPathError(errorCode,
        () => this.updateState({
          viewInfo: undefined,
          errorCode: errorCode,
          errorVizPath: this.props.vizPathFromUrl,
          hasRoutingError: true,
          hasDataAlertError: false,
          dataAlertRouterErrorProps: undefined,
          dataAlert: undefined
        }),
        this.props.vizPathFromUrl);
      return undefined;
    }
  }

  private getVizParamsThatRequireReload(routeLocationSearch: string): _.Dictionary<any> {
    let params = VizUrl.getVizParams(routeLocationSearch);
    params = _.omit(params, [UrlParamKeys.OriginalView]);
    return params;
  }

  public async componentDidUpdate(prevProps: VizSpaceProviderProps, prevState: VizSpaceProviderState) {
    const prevVizPathFromUrl = prevProps.vizPathFromUrl;
    const currVizPathFromUrl = this.props.vizPathFromUrl;
    const vizPathFromAppState = this.props.vizPath;

    let currVizParams = this.getVizParamsThatRequireReload(this.props.routeLocationSearch || '');
    let prevVizParams = this.getVizParamsThatRequireReload(prevProps.routeLocationSearch || '');
    const currInstanceId = VizUrl.getInstanceId(this.props.routeLocationSearch);
    const prevInstanceId = VizUrl.getInstanceId(prevProps.routeLocationSearch);

    // there was an instance id but now there isn't, reload the viz which will get an instance id
    const instanceIdWasRemoved = (!currInstanceId && !!prevInstanceId);

    const prevDataAlertId = VizUrl.getVizParams(prevProps.routeLocationSearch)[UrlParamKeys.EditDataAlert];
    const currDataAlertId = VizUrl.getVizParams(this.props.routeLocationSearch)[UrlParamKeys.EditDataAlert];

    if (prevDataAlertId !== currDataAlertId &&
      !_.isUndefined(this.state.viewInfo)  /** need to have a viewinfo to make sure the alert is for the right viz */) {
      if (!_.isUndefined(currDataAlertId)) {
        try {
          const dataAlert: IDataAlertInfo = await DataAlerts.getById(currDataAlertId);
          this.handleGetDataAlertResponse(dataAlert, this.state.viewInfo);
          return;
        } catch (e) {
          this.handleGetDataAlertReturnsError(this.state.viewInfo);
        }
      } else {
        // prevDataAlertId must be removed now so remove the data alert info from the state and continue to
        // rendering and fetching the viz information
        this.updateState({
          ...this.state,
          hasDataAlertError: false,
          dataAlert: undefined
        });
      }
    }

    // if the viz search parameters changed (except for instance id) OR an instance Id was removed,
    // reload the viz. We don't want to reload the viz every time the instance id changes completely because
    // that's what indicates that the viz loaded and this would cause an infinite loop.
    const vizParamsChanged = !_.isEqual(currVizParams, prevVizParams) || instanceIdWasRemoved;

    const dataAlertChanged = !_.isEqual(this.state.dataAlert, prevState.dataAlert);

    if ((prevVizPathFromUrl !== currVizPathFromUrl && currVizPathFromUrl !== vizPathFromAppState) || vizParamsChanged || dataAlertChanged) {
      // getting this angular scope stuff to work in unit tests is a headache. Use this prop to spy if we're here in unit tests.
      this.props.reloadVizSpy_forTestOnly && this.props.reloadVizSpy_forTestOnly();

      // the url changed (because the app state vizPath is different from the new route props)
      // but the vizPath is not updated yet. So update it and reload the viz.
      // if we are here just because the vizParamsChanged, it doesn't hurt to set the viz path anyway
      this.props.setVizPath(currVizPathFromUrl);

      if (this.spaceScope) {
        const previousViz: IViz = this.spaceScope.viz;


        this.spaceScope.viz = {
          path: currVizPathFromUrl,
          vizType: VizType.View,
          params: VizUrl.getVizParams(this.props.routeLocationSearch || ''),
          allowReloadViz: true,  // this needs to be true so we can reload the viz when the url changes
          siteUrlName: this.props.server.sessionSiteUrlName(),

          reloadViz: previousViz.reloadViz,

          instanceId: VizUrl.getInstanceId(this.props.routeLocationSearch),
          isExplicitOriginalViz: VizUrl.getIsExplicitOriginalVizFromUrl(window.location.href),
        };
        this.spaceScope.displayRoutingError = this.state.hasRoutingError;
        this.spaceScope.dataAlert = this.state.dataAlert;
        this.spaceScope.displayDataAlertRoutingError = this.state.hasDataAlertError;
        this.spaceScope.$apply();

        if (!this.spaceScope.displayDataAlertRoutingError && !this.spaceScope.displayRoutingError) {
          this.spaceScope.viz.reloadViz && this.spaceScope.viz.reloadViz();
        }

      } else {
        // since there is no spaceScope, that probably means we were showing the error page before.
        // fetch the view here, the data alert (if needed) and then keep going
        if (_.isUndefined(this.state.viewInfo)) {
          await this.getViewInfoAndUpdateBrowserTitleAndGetDataAlert();
        }
      }
    }
  }

  public shouldComponentUpdate(nextProps: VizSpaceProviderProps) {
    const nextVizPathFromUrl = nextProps.vizPathFromUrl;
    if (nextProps.vizPath !== VizPathInitialState &&
      nextProps.vizPath !== this.props.vizPath &&
      nextProps.vizPath !== nextVizPathFromUrl) {
        // vizPath changed via user clicking on viz tab (that's why the url viz path does not match the new app state vizPath-- see tbViz),
        // so don't rerender
        return false;
    }
    return true;
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as IVizViewerScope;
    childScope.updateVizProviderState = this.updateState;

    childScope.viz = {
      path: this.props.vizPathFromUrl,
      vizType: VizType.View,
      params: VizUrl.getVizParams(this.props.routeLocationSearch),
      siteUrlName: this.props.server.sessionSiteUrlName(),
      instanceId: VizUrl.getInstanceId(this.props.routeLocationSearch),
      isExplicitOriginalViz: VizUrl.getIsExplicitOriginalVizFromUrl(window.location.href),
      allowReloadViz: true
    };


    childScope.displayRoutingError = this.state.hasRoutingError;
    childScope.displayDataAlertRoutingError = this.state.hasDataAlertError;
    childScope.dataAlert = this.state.dataAlert;

    childScope.onVizSized = () => childScope.vizInitialized = true;

    childScope.vizProps = {};

    const siteUrls = new SpacesSiteUrls({siteUrlName: this.props.server.sessionSiteUrlName(), queryString: window.location.search});
    const handleDevicePreviewClick = () => {
      Telemetry.sendEvent<DevicePreviewShowPreviewEvent>({
        type: TelemetryEvents.DevicePreviewShowPreview
      });

      DevicePreviewModalBuilder.openPreview(this.props.angularContext, childScope.viz.path);
    };

    childScope.vizViewerWidgetsBarProps = {
      view: null,
      siteUrls: siteUrls,
      dataSources: this.props.dataSources,
      showDevicePreview: false,
      handleDevicePreviewClick: handleDevicePreviewClick,
      layoutType: selectLayoutType(appStore.getState()),
    };

    function displayInvalidVizError(errorCode: number, vizPath: string) {
      childScope.displayRoutingError = true;
      childScope.displayDataAlertRoutingError = false;

      childScope.updateVizProviderState({
        hasRoutingError: true,
        errorCode: errorCode,
        errorVizPath: vizPath,
        viewInfo: undefined,
        hasDataAlertError: false,
        dataAlertRouterErrorProps: undefined,
      });
    }

    childScope.$watch('viz.path', (newPath: string) => {

      if(!newPath) {
        displayInvalidVizError(CommonErrorCode.UnexpectedParameterValue, newPath);
        return;
      }

      const locationService = ServiceLocator.get<ng.ILocationService>('$location');
      const searchParams = locationService.search();

      if (this.firstViewInfoOfPageLoad !== undefined && newPath === this.firstViewInfoOfPageLoad.path) {
        // TFS 1040069: This is weird but we fetch the viewInfo when we first load the page in the provider.
        // When we set up the scope the first time, we don't need to fetch the viewInfo again, only later
        // when the path changes. We need to reset the firstViewInfoOfPageLoad to make sure that if the path
        // changes *back* to the first one (e.g., if someone is clicking around on the viz tabs), we refetch
        // the viewInfo.
        this.handleNewViewInfo(this.firstViewInfoOfPageLoad, childScope, searchParams);
        this.firstViewInfoOfPageLoad = undefined; // reset now that we have used this

      } else {
        Views.getByPath(newPath)
          .then((viewInfo: IViewDetailInfo) => {
            this.handleNewViewInfo(viewInfo, childScope, searchParams);
          })
          .catch((errorCode: number) => {
            // B153202, TFSID: 463801 - If user does not have permission to view the viz but guest does
            // then we receive a GuestPermittedViewNotFoundError, so we can try to automatically
            // redirect to to the embedded view (if not a site or server admin). But if there's a regular
            // ViewNotFound error, there's no chance for guest to be able to view it, so just show the error here and now.
            this.handleViewGetByPathError(errorCode, displayInvalidVizError, newPath);
          });
      }
    });

    childScope.$watch('viz.instanceId', (newInstanceId: string) => {
      if (newInstanceId) {
        this.props.location.replace().search(VizService.InstanceIdKey, newInstanceId);
      }
    });

    childScope.$watch('viz.isExplicitOriginalViz', (isExplicitOriginalViz: boolean) => {
      this.props.location.replace().search(VizService.ExplicitOriginalVizParamKey, isExplicitOriginalViz ? 'y' : null);
    });

    this.spaceScope = childScope;
    return childScope;
  }

  private handleViewGetByPathError(errorCode: number, displayInvalidVizError: (errorCode: number, vizPath: string) => void, newPath: string) {
    if (errorCode === CommonErrorCode.GuestPermittedViewNotFound && ServerSettingsUnauthenticated.isGuestEnabled() && !this.props.server.isSessionSiteOrServerAdmin()) {
      this.props.vizService.redirectToEmbeddedMode(false);
    }
    else {
      displayInvalidVizError(errorCode, newPath);
    }
  }

  private handleNewViewInfo(viewInfo: IViewDetailInfo, childScope: IVizViewerScope, searchParams: any) {
    this.loadNewViewInfo(viewInfo, childScope);
    if (childScope.displayRoutingError && !childScope.displayDataAlertRoutingError) {
      // We display routing error when we could not fetch the view. Since we could fetch it now,
      // reset the error codes and  update the state since fetching this view was successful.
      // If we displayed a data alert error, that means we fetched the view correctly before.
      childScope.displayRoutingError = false;
      childScope.updateVizProviderState({
        ...initialNoErrorState,
        viewInfo: viewInfo
      });
    }
    // because the viewInfo can return after we have navigated away from this page, we only want to call
    // enterSpace if we are still on the view page. Otherwise, all sorts of errors happen if the SpaceInfo
    // is wrong- TFSID 956506
    if (isCurrentUrlViz()) {
      appStore.dispatch(enterSpace({
        item: viewInfo,
        spaceType: ViewDefinition.contentType,
        subspaceName: undefined
      }));
    }
    const alertId = searchParams[UrlParamKeys.EditDataAlert];
    //if fromEmail param in location.search then log the event
    if (searchParams[UrlParamKeys.FromFailureEmail]) {
      sendVisitAlertFromEmailTelemetry({
        alert_evaluation_state: 'Failure',
        alert_id: alertId,
        view_id: viewInfo.id,
        workbook_id: viewInfo.workbook.id,
        site_role: this.props.server.sessionSiteRole(),
      });
    }
    if (searchParams[UrlParamKeys.FromSuccessEmail]) {
      sendVisitAlertFromEmailTelemetry({
        alert_evaluation_state: 'Success',
        alert_id: alertId,
        view_id: viewInfo.id,
        workbook_id: viewInfo.workbook.id,
        site_role: this.props.server.sessionSiteRole(),
      });
    }
    maybeSendTelemetry_favoriteItemOpened(viewInfo);
  }

  private displayDataAlertError(dataAlertRouterErrorProps: RouterErrorOwnProps, viewInfo: IViewDetailInfo) {

    this.updateState({
      hasRoutingError: false,
      errorCode: undefined,
      errorVizPath: undefined,
      viewInfo: viewInfo,
      hasDataAlertError: true,
      dataAlertRouterErrorProps,
      dataAlert: undefined
    });
  }

  private handleGetDataAlertReturnsError(viewInfo: IViewDetailInfo): void {
    const viewUrl = this.props.urls.viewUrl(this.props.vizPathFromUrl);
    const routerErrorProps: RouterErrorOwnProps = {
      errorType: 'view-data-alert-exists',
      urls: this.props.urls,
      dataAlertErrorData: {
        viewUrl: viewUrl,
        owner: undefined
      }
    };
    this.displayDataAlertError(routerErrorProps, viewInfo);
  }

  private handleGetDataAlertResponse(dataAlert: IDataAlertInfo, viewInfo: IViewDetailInfo): void {
    //Using siteUrls.viewUrl($location.path()) would preserve query string, which won't work
    //So we're explicitly getting the view URL and explicity manually adding the hash
    const viewUrl = this.props.urls.viewUrl(this.props.vizPathFromUrl);
    //we don't want an alert error if there's a view error
    if (!!this.state.hasRoutingError && _.isUndefined(viewInfo)) {
      return;
    }
    if (!dataAlert) {
      const routerErrorProps: RouterErrorOwnProps = {
        errorType: 'view-data-alert-exists',
        urls: this.props.urls,
        dataAlertErrorData: {
          viewUrl: viewUrl,
          owner: undefined
        }
      };
      this.displayDataAlertError(routerErrorProps, viewInfo);
    }
    else if (dataAlert.owner.id !== this.props.server.sessionUserId() && !this.props.server.isSessionSiteOrServerAdmin()) {
      const routerErrorProps: RouterErrorOwnProps = {
        errorType: 'view-data-alert-permissions',
        urls: this.props.urls,
        dataAlertErrorData: {
          viewUrl: viewUrl,
          owner: dataAlert.owner
        }
      };
      this.displayDataAlertError(routerErrorProps, viewInfo);
    }
    else if (this.props.server.isViewerUser()) {
      const routerErrorProps: RouterErrorOwnProps = {
        errorType: 'view-data-alert-viewer',
        urls: this.props.urls,
        dataAlertErrorData: {
          viewUrl: viewUrl,
          owner: undefined
        }
      };
      this.displayDataAlertError(routerErrorProps, viewInfo);
    }
    else {
      // dataAlert successfully fetched and user has access to it-- make sure all errors are reset
      this.updateState({
        ...this.state,
        hasRoutingError: false,
        hasDataAlertError: false,
        dataAlertRouterErrorProps: undefined,
        viewInfo: viewInfo,
        dataAlert: dataAlert
      });
    }
  }

  private loadNewViewInfo(viewInfo: IViewDetailInfo, childScope: any): ng.IPromise<any> {

    return this.props.projects.getProjectHierarchy(viewInfo.workbook.project.id).then((projectHierarchy: INamedItemInfo[]) => {
      BrowserTitleService.setVizTitle(viewInfo.name, viewInfo.workbook.name);
      childScope.vizProps.view = viewInfo;

      // Update breadcrumbs
      const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
      const siteUrls = this.props.urls;
      const workbook: IExplorableItemTypeInfo<'workbook'> = {
        id: viewInfo.workbook.id,
        name: viewInfo.workbook.name,
        contentType: 'workbook'
      };

      // because the project hierarchy can return after we have navigated away from this page, we only want to call
      // populateContentBreadcrumbs if we are still on the view page. Otherwise, the wrong breadcrumbs show.
      // TFSID 956506
      if (isCurrentUrlViz()) {
        populateContentBreadcrumbs([...projectHierarchyWithType, workbook, viewInfo], siteUrls);
      }

      Views.markRecentlyViewed(viewInfo.id);

      return Views.actions(viewInfo.id).then((actions: IViewActions) => {
        childScope.allowedAction = actions;
      });
    });
  }

  private updateState = (newState: Pick<VizSpaceProviderState, keyof VizSpaceProviderState>): void => {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

export namespace VizSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const server = ServiceLocator.get<ServerService>('ServerService');
    const dataSources = ServiceLocator.get<DataSources>('DataSources');
    const vizService = ServiceLocator.get<VizService>('VizService');
    const location = ServiceLocator.get<ng.ILocationService>('$location');

    return {
      server,
      dataSources,
      vizService,
      projects: Projects,
      location
    };
  }

  function selectPropsFromAngularServices(angularServices: VizSpaceProviderAngularProps) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<VizSpaceProviderPropsWithoutAngular, VizSpaceProviderAngularProps, VizSpaceProviderAngularProps>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const VizSpaceProviderAng: React.ComponentType<VizSpaceProviderPropsWithoutAngular> = VizSpaceProviderAngularProvider.HOC(VizSpaceProvider);

const mapStateToProps: MapStateToProps<VizSpaceProviderStateProps> = (appState) => ({
  vizPath: selectVizPath(appState),
  sessionSiteUrlName: SessionSiteSelectors.siteUrlName(appState),
  layoutType: selectLayoutType(appState),
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(appState)
});

const mapDispatchToProps: MapDispatchToProps<VizSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setVizPath,
  setSpaceInfoItem,
}, dispatch);

export const ConnectedVizSpaceProvider: React.ComponentClass<VizSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(VizSpaceProviderAng);
